import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Kharghar from "../assets/about-img.jpeg";
import Bangalore from "../assets/bangalore-loc.png";
import Pune from "../assets/Pune.png";
import Shirdi from "../assets/shirdi-loc.PNG"

gsap.registerPlugin(ScrollTrigger);

const centers = [
  {
    city: "Navi Mumbai",
    title: "Hair Transplant in Navi Mumbai",
    doctor: "Dr. Bhavesh Gupta",
    desc: "Clinique Internationale in Navi Mumbai offers advanced hair transplant services using the latest techniques. With a focus on patient care and safety, we provide a comfortable environment for hair restoration.",
    tag: "Kharghar",
    image: Kharghar,
    url: "hair-transplant-in-mumbai.php",
  },
  {
    city: "Bangalore",
    title: "Hair Transplant in Bangalore",
    doctor: "Dr. Idris MD",
    desc: "For a hair transplant in Bangalore, consider Clinique Internationale led by Dr. Idris MD. With a commitment to excellence, we specialize in delivering 100% natural-looking results for hair loss patients.",
    tag: "Bengaluru",
    image: Bangalore,
    url: "bangalore",
  },
  {
    city: "Pune",
    title: "Hair Transplant in Pune",
    doctor: "Dr. Ajinkya Patil",
    desc: "Opt for excellence in hair transplants in Pune with Dr. Ajinkya Patil. Our commitment is to deliver the best natural results, prioritizing expertise and aesthetics for a transformative experience.",
    tag: "Pune",
    image: Pune,
    url: "pune",
  },
  {
    city: "Shirdi",
    title: "Hair Transplant in Shirdi",
    doctor: "Dr. Pranay Thakur",
    desc: "Choose excellence in hair transplants in Shirdi with Dr. Pranay Thakur. Located in the holy city of Shirdi, trust Clinique Internationale for unparalleled hair transplant services.",
    tag: "Shirdi",
    image: Shirdi,
    url: "shirdi",
  },
];

export default function OurCenters() {
  const sectionRef  = useRef(null);
  const eyebrowRef  = useRef(null);
  const headingRef  = useRef(null);
  const subRef      = useRef(null);
  const cardRefs    = useRef([]);
  const mapPinRefs  = useRef([]);
  const titleRefs   = useRef([]);
  const descRefs    = useRef([]);
  const cityRefs    = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // ── BG SCRUB: white → black as section scrolls into view ──
      gsap.fromTo(sectionRef.current,
        { backgroundColor: "#ffffff" },
        {
          backgroundColor: "#080600",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 90%",
            end: "top 10%",
            scrub: true,
          },
        }
      );

      // Heading color scrub: dark → white
      gsap.fromTo(headingRef.current,
        { color: "#1a0f02" },
        {
          color: "#ffffff",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "top 10%",
            scrub: true,
          },
        }
      );

      // Subtitle color scrub: dark brown → white/45%
      gsap.fromTo(subRef.current,
        { color: "#6b5535" },
        {
          color: "rgba(255,255,255,0.45)",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "top 10%",
            scrub: true,
          },
        }
      );

      // Card bg + border scrub: light card → dark glass card
      cardRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { backgroundColor: "#ffffff", borderColor: "rgba(160,120,40,0.18)" },
          {
            backgroundColor: "rgba(255,255,255,0.025)",
            borderColor: "rgba(201,168,76,0.16)",
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "top 10%",
              scrub: true,
            },
          }
        );
      });

      // Card title scrub: dark → white
      titleRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { color: "#1a0f02" },
          {
            color: "#ffffff",
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "top 10%",
              scrub: true,
            },
          }
        );
      });

      // Card desc scrub: brown → white/48%
      descRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { color: "#6b5535" },
          {
            color: "rgba(255,255,255,0.48)",
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "top 10%",
              scrub: true,
            },
          }
        );
      });

      // Card city label scrub
      cityRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { color: "rgba(26,15,2,0.35)" },
          {
            color: "rgba(255,255,255,0.25)",
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "top 10%",
              scrub: true,
            },
          }
        );
      });

      // ── ENTRANCE ANIMATIONS ──
      gsap.fromTo(eyebrowRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: eyebrowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );

      gsap.fromTo(headingRef.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out",
          scrollTrigger: { trigger: headingRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );

      gsap.fromTo(subRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: subRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
      );

      // Staggered fade-and-rise entrance for cards, one after another
      gsap.fromTo(cardRefs.current,
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.18,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      mapPinRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, scale: 0 },
          {
            opacity: 1, scale: 1, duration: 0.5, ease: "back.out(2)",
            delay: 0.06 * i,
            scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none reverse" },
          }
        );
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:wght@300;400;500&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .oc-card {
          transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.45s cubic-bezier(0.22, 1, 0.36, 1),
                      border-color 0.45s ease;
        }
        .oc-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 22px 44px rgba(0,0,0,0.28), 0 0 0 1px rgba(201,168,76,0.35);
          border-color: rgba(201,168,76,0.55) !important;
        }

        .oc-img-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 16px 16px 0 0;
          height: 400px;
        }
        .oc-img {
          transform: scale(1.15);
          transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .oc-card:hover .oc-img {
          transform: scale(1);
        }

        .oc-sweep {
          position: absolute;
          inset: 0;
          background: linear-gradient(115deg, transparent 20%, rgba(201,168,76,0.55) 45%, rgba(255,241,199,0.75) 52%, rgba(201,168,76,0.55) 59%, transparent 80%);
          transform: translateX(-140%);
          opacity: 0;
          transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
          mix-blend-mode: overlay;
        }
        .oc-card:hover .oc-sweep {
          transform: translateX(140%);
          opacity: 1;
        }

        .oc-fade-item {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .oc-card:hover .oc-fade-item {
          opacity: 0.92;
          transform: translateY(-4px);
        }
        .oc-card:hover .oc-fade-1 { transition-delay: 0.03s; }
        .oc-card:hover .oc-fade-2 { transition-delay: 0.07s; }
        .oc-card:hover .oc-fade-3 { transition-delay: 0.11s; }
        .oc-card:hover .oc-fade-4 { transition-delay: 0.15s; }

        .oc-arrow {
          transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .oc-card:hover .oc-arrow {
          transform: translateX(6px);
        }
          @media (max-width: 639px) {
  .oc-img-wrap {
    height: auto !important;
    aspect-ratio: 4 / 5 !important;      /* taller image, adjust to taste */
    margin: 0 -0.75rem 0.75rem !important;
    width: calc(100% + 1.5rem) !important;
    border-radius: 12px 12px 0 0;
  }
  .oc-img {
    object-fit: cover !important;        /* fills the taller frame, no empty bars */
  }
  .oc-tag    { font-size: 8px !important; padding: 3px 8px !important; }
  .oc-title  { font-size: 12px !important; letter-spacing: 0.02em !important; }
  .oc-doctor { font-size: 10px !important; }
  .oc-desc   { display: none !important; }   /* description hidden on mobile */
  .oc-link   { font-size: 10px !important; letter-spacing: 0.06em !important; }
}
      `}</style>

      <section
        ref={sectionRef}
        className="relative w-full overflow-hidden py-24 md:py-28"
        style={{ backgroundColor: "#ffffff" }} // initial state, GSAP scrubs this
      >
        {/* layered ambient glows */}
        <div
          className="absolute top-[-10%] left-[-5%] w-1/2 h-3/5 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[-15%] right-[-5%] w-[55%] h-2/3 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)" }}
        />

        {/* dotted texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(201,168,76,0.07) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 75%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">

          {/* ── HEADER ── */}
          <div className="text-center mb-16">
            <p
              ref={eyebrowRef}
              className="opacity-0 mb-3.5 uppercase"
              style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.32em", color: "#c9a84c" }}
            >
              Clinique Internationale
            </p>

            <h2
              ref={headingRef}
              className="opacity-0 font-black uppercase mb-4"
              style={{
                fontFamily: "'Cinzel', serif",
                fontSize: "clamp(1.8rem, 3.4vw, 2.8rem)",
                letterSpacing: "0.05em",
              }}
            >
              Our Centers
            </h2>

            <div
              className="mx-auto mb-5"
              style={{ width: "56px", height: "1px", background: "linear-gradient(90deg, transparent, #c9a84c, transparent)" }}
            />

            <p
              ref={subRef}
              className="opacity-0 mx-auto"
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 300,
                fontSize: "15px",
                lineHeight: 1.75,
                maxWidth: "560px",
              }}
            >
              Four destinations, one promise of excellence <br/> find your nearest Clinique
              Internationale hair restoration center across India.
            </p>
          </div>

          {/* ── GRID ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-7">
            {centers.map((c, i) => (
              <div
                key={i}
                ref={el => cardRefs.current[i] = el}
                className="oc-card relative opacity-0 rounded-2xl border p-3 pb-4 sm:p-6 sm:pb-7 overflow-hidden group"
                style={{ borderColor: "rgba(160,120,40,0.18)" }}
              >
                {/* corner accent glow */}
                <div
                  className="absolute top-0 right-0 w-16 h-16 pointer-events-none"
                  style={{ background: "radial-gradient(circle at top right, rgba(201,168,76,0.18), transparent 70%)" }}
                />

                {/* ── IMAGE BANNER ── */}
                <div className="oc-img-wrap" style={{ aspectRatio: "4 / 3", margin: "0 -1.25rem 1.5rem", width: "calc(100% + 2.5rem)" }}>
                  <img
                    src={c.image}
                    alt={c.title}
                    className="oc-img w-full h-full object-contain block"
                    loading="lazy"
                  />
                  <div className="oc-sweep" />
                </div>

                {/* top row */}
                <div className="flex items-center justify-center sm:justify-between mb-3 sm:mb-6">
                  <div
                    ref={el => mapPinRefs.current[i] = el}
                    className="hidden sm:flex items-center justify-center rounded-full shrink-0"
                    style={{
                      width: "38px", height: "38px",
                      background: "rgba(201,168,76,0.1)",
                      border: "1px solid rgba(201,168,76,0.3)",
                      transform: "scale(0)",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 21s-7-7.2-7-12a7 7 0 1114 0c0 4.8-7 12-7 12z" stroke="#c9a84c" strokeWidth="1.8" strokeLinejoin="round"/>
                      <circle cx="12" cy="9" r="2.4" stroke="#c9a84c" strokeWidth="1.8"/>
                    </svg>
                  </div>

                  <span
                    className="oc-tag px-3 py-1 rounded-full uppercase"
                    style={{
                      fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
                      color: "#c9a84c", border: "1px solid rgba(201,168,76,0.25)",
                      background: "rgba(201,168,76,0.06)",
                    }}
                  >
                    {c.tag}
                  </span>
                </div>

                {/* title */}
                <h3
                  ref={el => titleRefs.current[i] = el}
                  className="oc-title oc-fade-item oc-fade-1 font-bold uppercase mb-1.5"
                  style={{ fontFamily: "'Cinzel', serif", fontSize: "16px", letterSpacing: "0.04em", lineHeight: 1.3 }}
                >
                  {c.title}
                </h3>

                {/* doctor */}
                <p
                  className="oc-doctor oc-fade-item oc-fade-2 mb-2 sm:mb-4"
                  style={{ fontFamily: "'Raleway', sans-serif", fontSize: "12px", fontWeight: 500, color: "#c9a84c", letterSpacing: "0.04em" }}
                >
                  Led by {c.doctor}
                </p>

                {/* desc */}
                <p
                  ref={el => descRefs.current[i] = el}
                  className="oc-desc oc-fade-item oc-fade-3"
                  style={{ fontFamily: "'Raleway', sans-serif", fontSize: "13.5px", fontWeight: 300, lineHeight: 1.8 }}
                >
                  {c.desc}
                </p>

                {/* footer */}
                <div
                  className="oc-fade-item oc-fade-4 mt-3 sm:mt-6 pt-3 sm:pt-4 flex items-center justify-between border-t"
                  style={{ borderColor: "rgba(201,168,76,0.1)" }}
                >
                  <a
                    href={c.url}
                    className="oc-link flex items-center gap-1.5"
                    style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.1em", color: "#c9a84c", textDecoration: "none" }}
                  >
                    Explore Center
                    <svg className="oc-arrow" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                    </svg>
                  </a>
                  <span className="hidden sm:inline"
                    ref={el => cityRefs.current[i] = el}
                    style={{ fontFamily: "'Raleway', sans-serif", fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    {c.city}
                  </span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>
    </>
  );
}